/**
 * Which building this desk is working in. The list comes from the server
 * (GET /reception/me, own buildings only); the choice is kept per person on the
 * device. No provider: any screen reads it and they stay in step.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, getSignedInUserId, httpStatus } from '@/utils/api';

export interface DeskBuilding {
  id: string; name: string; zone?: string; desk_open: string; desk_close: string; auto_checkout: boolean;
  printer_name: string | null; printer_ok: boolean;
}

export interface DeskMe {
  buildings: DeskBuilding[]; lobby: boolean; company: string; escalation_minutes: number;
  privacy_notice: string; notice_version: number; ic_line: string | null; posh_notice: string | null;
  id_types: string[]; admit_reasons: string[];
}

const keyFor = () => `reception-building:${getSignedInUserId() ?? 'none'}`;
const meKey = () => `reception-me:${getSignedInUserId() ?? 'none'}`;
const listeners = new Set<() => void>();
const read = () => { try { return localStorage.getItem(keyFor()); } catch { return null; } };

export function chooseDeskBuilding(id: string): void {
  try { localStorage.setItem(keyFor(), id); } catch { /* full: the choice lasts this page */ }
  listeners.forEach((l) => l());
}

export function useDeskMe() {
  return useQuery({
    queryKey: ['reception', 'me'],
    queryFn: async () => {
      try {
        const me = await apiGet<DeskMe>('/reception/me');
        try { localStorage.setItem(meKey(), JSON.stringify(me)); } catch { /* full: only the offline copy is older */ }
        return me;
      } catch (e) {
        // Offline (C34): the desk still needs its building to read today's saved list.
        const status = httpStatus(e);
        const kept = status === 0 || status === 502 || status === 503 || status === 504 ? localStorage.getItem(meKey()) : null;
        if (kept) return JSON.parse(kept) as DeskMe;
        throw e;
      }
    },
    networkMode: 'always',
    staleTime: 60_000,
  });
}

/** The chosen building: the remembered one while it is still on the list, else
 *  the first. Null only while the list is loading or empty. */
export function useDeskBuilding(): DeskBuilding | null {
  const me = useDeskMe();
  const chosen = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, read, () => null);
  const list = me.data?.buildings ?? [];
  const current = list.find((b) => b.id === chosen) ?? list[0] ?? null;
  useEffect(() => {
    if (current && current.id !== chosen) chooseDeskBuilding(current.id);
  }, [current, chosen]);
  return current;
}
